import React, { useEffect, useState, useCallback } from 'react';
import { getMyStore, addProduct, updateProduct, deleteProduct } from '../../services/api';
import { Btn, toast, useConfirm, Card, Spinner } from '../../components/shared/UI';
import ProductForm from '../../components/shared/ProductForm';

const MAX = 30;

export default function ClientProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'new' | product
  const [saving, setSaving]     = useState(false);
  const { confirm, Dialog }     = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    // GET api/client/store → { config, productos }
    getMyStore()
      .then(s => setProducts(s.productos || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (modal === 'new') {
        // POST api/client/store/productos
        await addProduct(data);
        toast('Producto creado ✅');
      } else {
        // PUT api/client/store/productos/:id
        await updateProduct(modal.id, data);
        toast('Producto actualizado ✅');
      }
      setModal(null);
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    const ok = await confirm(`¿Eliminar "${p.nombre}"? Esta acción no se puede deshacer.`);
    if (!ok) return;
    try {
      // DELETE api/client/store/productos/:id
      await deleteProduct(p.id);
      toast('Producto eliminado');
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <div>
      <Dialog />
      <ProductForm
        open={!!modal}
        onClose={() => setModal(null)}
        onSave={handleSave}
        initial={modal && modal !== 'new' ? modal : null}
        loading={saving}
      />

      {/* Header */}
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12 }}>
        <div>
          <h1 style={{ color:'#fff',fontSize:24,fontFamily:"'Syne',sans-serif",fontWeight:800,margin:'0 0 5px' }}>
            Mis Productos
          </h1>
          <p style={{ color:'rgba(255,255,255,0.38)',fontSize:13,margin:0 }}>
            {products.length}/{MAX} productos
          </p>
        </div>
        {products.length < MAX && (
          <Btn onClick={() => setModal('new')}>+ Agregar producto</Btn>
        )}
      </div>

      {/* Limit warning */}
      {products.length >= MAX && (
        <div style={{
          background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',
          borderRadius:12,padding:'13px 18px',marginBottom:18,
          color:'#fca5a5',fontSize:13,
        }}>
          ⚠️ Alcanzaste el límite de {MAX} productos. Eliminá alguno para agregar más.
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display:'flex',justifyContent:'center',padding:60 }}>
          <Spinner />
        </div>
      ) : products.length === 0 ? (
        <Card>
          <div style={{ textAlign:'center',padding:'48px 20px' }}>
            <div style={{ fontSize:56,marginBottom:12 }}>📦</div>
            <p style={{ color:'rgba(255,255,255,0.45)',marginBottom:18,fontSize:15 }}>
              Todavía no tenés productos en tu tienda.
            </p>
            <Btn onClick={() => setModal('new')}>+ Crear el primero</Btn>
          </div>
        </Card>
      ) : (
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:14 }}>
          {products.map(p => (
            <div key={p.id} style={{
              background:'rgba(255,255,255,0.04)',
              border:'1px solid rgba(255,255,255,0.08)',
              borderRadius:14,overflow:'hidden',
              transition:'transform 0.18s,border-color 0.18s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.borderColor='rgba(124,58,237,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'; }}
            >
              {/* Image */}
              <div style={{ position:'relative',height:148,background:'rgba(255,255,255,0.04)',overflow:'hidden' }}>
                {p.imagen ? (
                  <img
                    src={p.imagen}
                    alt={p.nombre}
                    style={{ width:'100%',height:'100%',objectFit:'cover',transition:'transform 0.3s' }}
                    onError={e => { e.target.style.display='none'; }}
                  />
                ) : (
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',fontSize:40,color:'rgba(255,255,255,0.15)' }}>
                    👟
                  </div>
                )}
                {p.destacado && (
                  <div style={{ position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.65)',borderRadius:7,padding:'3px 9px',fontSize:11,color:'#fde047' }}>
                    ⭐ Destacado
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding:'13px 14px 14px' }}>
                <div style={{ color:'rgba(255,255,255,0.38)',fontSize:11,marginBottom:3 }}>
                  {p.categoria || 'Sin categoría'}
                </div>
                <div style={{
                  color:'#fff',fontWeight:600,fontSize:14,marginBottom:4,
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                }}>
                  {p.nombre}
                </div>
                {p.descripcion && (
                  <div style={{
                    color:'rgba(255,255,255,0.35)',fontSize:12,marginBottom:6,
                    display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:1.4,
                  }}>
                    {p.descripcion}
                  </div>
                )}
                <div style={{ color:'#a78bfa',fontWeight:700,fontSize:16,marginBottom:12 }}>
                  ${Number(p.precio).toLocaleString('es-AR')}
                </div>
                <div style={{ display:'flex',gap:7 }}>
                  <Btn size="sm" variant="ghost" style={{ flex:1 }} onClick={() => setModal(p)}>
                    ✏️ Editar
                  </Btn>
                  <Btn size="sm" variant="danger" onClick={() => handleDelete(p)}>
                    🗑
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
